import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { shouldShowEquinePremiumAsTbc } from '../../equine-premium-tbc.util';
import { getEquineDisplayPremiumData } from '../../equine-utils';

@Component({
  selector: 'app-equine-quote-financials',
  templateUrl: './equine-quote-financials.component.html',
  styleUrls: ['./equine-quote-financials.component.css']
})
export class EquineQuoteFinancialsComponent implements OnChanges{
  @Input() quoteDetails: any = {};
  @Input() isBroker = false;

  @Input() adjustedPremiumDetails: any = null;
  @Input() adjustmentValues: any = null;
  @Input() adjustmentSaving = false;
  @Output() adjustmentChange = new EventEmitter<any>();
  @Output() adjustmentSave = new EventEmitter<any>();

  adjustments: any = {
    premium_loading_percent: null,
    premium_loading_fixed: null,
    premium_loading_reason: '',
    premium_discount_percent: null,
    premium_discount_fixed: null,
    premium_discount_reason: '',
    overwrite_underwriting_fee: null
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['adjustmentValues'] || changes['quoteDetails']) {
      this.syncAdjustmentsFromInputs();
    }
  }

  private syncAdjustmentsFromInputs(): void {
    const fin = this.quoteDetails?.financial_details || {};
    const fromQuote = this.quoteDetails?.premium_adjustments || {};
    const fromInput = this.adjustmentValues || {};

    this.adjustments = {
      premium_loading_percent: this.coerceAdjustmentNumber(
        fromInput.premium_loading_percent,
        fromQuote.premium_loading_percent,
        fin.premium_loading_percent,
        fin.premium_loading_percentage
      ),
      premium_loading_fixed: this.coerceAdjustmentNumber(
        fromInput.premium_loading_fixed,
        fromQuote.premium_loading_fixed,
        fin.premium_loading_fixed
      ),
      premium_loading_reason:
        fromInput.premium_loading_reason ??
        fromQuote.premium_loading_reason ??
        fin.premium_loading_reason ??
        '',
      premium_discount_percent: this.coerceAdjustmentNumber(
        fromInput.premium_discount_percent,
        fromQuote.premium_discount_percent,
        fin.premium_discount_percent
      ),
      premium_discount_fixed: this.coerceAdjustmentNumber(
        fromInput.premium_discount_fixed,
        fromQuote.premium_discount_fixed,
        fin.premium_discount_fixed
      ),
      premium_discount_reason:
        fromInput.premium_discount_reason ??
        fromQuote.premium_discount_reason ??
        fin.premium_discount_reason ??
        '',
      overwrite_underwriting_fee: this.coerceAdjustmentNumber(
        fromInput.overwrite_underwriting_fee,
        fromQuote.overwrite_underwriting_fee,
        fin.overwrite_underwriting_fee
      ),
    };
  }

  private coerceAdjustmentNumber(...values: unknown[]): number | null {
    for (const value of values) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      const n = Number(value);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return null;
  }

  get underwritingNotes(): string {
    return this.quoteDetails?.underwriting_notes || '';
  }

  get referredReason(): string {
    return this.quoteDetails?.manually_referred_reason || '';
  }

  get data(): any {
    return getEquineDisplayPremiumData(this.quoteDetails, this.adjustedPremiumDetails);
  }

  get policyPremium(): number {
    const d = this.data;
    if (!d) return 0;
    return Number(d.policy_premium ?? 0);
  }

  get ipt(): number {
    const d = this.data;
    if (!d) return 0;
    return Number(d.ipt ?? d.ipt_value ?? d.ipt_fee ?? 0);
  }

  get totalPremiumInclIpt(): number {
    const d = this.data;
    if (!d) return 0;
    return Number(d.total_premium ?? 0);
  }

  get uwFee(): number {
    const d = this.data;
    if (!d) return 0;
    return Number(d.uw_fee ?? d.fees ?? 0);
  }

  get totalAmountPayable(): number {
    const d = this.data;
    if (!d) return 0;
    return Number(d.total_payable_amount ?? d.total_policy_cost ?? 0);
  }

  get showPremiumAsTbc(): boolean {
    return shouldShowEquinePremiumAsTbc(this.quoteDetails);
  }

  onAdjustmentsChange(): void {
    this.adjustmentChange.emit({
      ...this.adjustments
    });
  }

  onSaveAdjustments(): void {
    this.adjustmentSave.emit({
      ...this.adjustments
    });
  }
}
